import {
  FieldOptions,
  FieldProperties,
  FieldStyles
} from './fieldHelperFunctions';
import Field from './entities/Field';
import { InlineErrors } from './inlineErrors';
import SimplifiedProduct from '../integrations/stripe/SimplifiedProduct';
import Cart from '../integrations/stripe/Cart';
import Collaborator from './entities/Collaborator';
import FeatheryClient from './featheryClient';
import { LogicRule } from '../types/Form';
import AssistantClient from '../assistant/AssistantClient';
import {
  ExtractionActionOptions,
  FillQuikParams,
  ForwardInboxEmailOptions,
  PageSelectionInput
} from '@feathery/client-utils';

export type AlloyEntities = Record<string, any>[];
export type LoanProCustomerObject = Record<string, any>;
export type RunComputerAgentOptions = {
  waitForCompletion?: boolean;
  onStatusUpdate?: (data: any) => void;
  onComplete?: (data: any) => void;
};
export type GetConfigParams = {
  filter?: Record<string, any>;
  keys?: string[];
  unique?: boolean;
};
export type GetConfig = ({
  filter,
  keys,
  unique
}: GetConfigParams) => Promise<Record<string, any>[]>;
// A phone number for an authentication method: an E.164 string, or the split
// form ({ countryCode, number }) that identity_verification requires.
type DocusignPhoneNumber = string | { countryCode: string; number: string };
// Identity verification DocuSign requires before the signer opens the
// envelope. Each method must be enabled on the DocuSign account.
type DocusignSignerAuthentication = {
  method:
    | 'access_code'
    | 'sms'
    | 'phone'
    | 'knowledge_based'
    | 'identity_verification';
  // Required for 'sms'; for 'phone' unless recipientMayProvideNumber; optional
  // for an 'identity_verification' workflow with a phone step
  phoneNumbers?: DocusignPhoneNumber[];
  accessCode?: string; // required for 'access_code'
  // Send the access code in DocuSign's notification email (defaults false)
  addAccessCodeToEmail?: boolean;
  // 'phone' only: let the recipient enter their own number
  recipientMayProvideNumber?: boolean;
  // Required for 'identity_verification' - a workflow GUID from the account's
  // DocuSign ID Verification settings
  workflowId?: string;
};
type DocusignSigner = {
  // Required for esign signers; omit for paper signers (not a DocuSign recipient)
  email?: string;
  name: string;
  // 'esign' (default) signs electronically; 'paper' is excluded from the
  // envelope (no recipient/tabs) for offline signing
  signMethod?: 'esign' | 'paper';
  // Optional signing-order override; equal values sign in parallel
  routingOrder?: string;
  // Document visibility: 0-based document indices to hide from this signer
  excludedDocuments?: number[];
  // Identity verification this signer must clear to open the envelope
  authentication?: DocusignSignerAuthentication;
};
// A document entry can be a plain template UUID string, or an object form for
// multi-instance envelopes: fill a template fresh (documentId + fillData) or
// reuse a previously generated envelope (envelopeId). signerMap routes a
// template field's signer_index -> an index in `signers`.
type DocusignDocument =
  | string
  | {
      documentId?: string;
      envelopeId?: string;
      fillData?: Record<string, any>;
      signerMap?: Record<string, number>;
      // Selects which repeating/array field value fills this instance
      // (0-based), so the same template can be sent once per repetition
      repeatIndex?: number;
    };
type DocusignLibraryDocuments = {
  library: 'quik';
  groups: {
    forms: { id: string }[];
    rolePrefixes: string[];
    index: number;
  }[];
  field_mapping: { roleField: string; featheryField: string }[];
};
// Reminder + expiration schedule (day counts). Omit either block to leave that
// part on the DocuSign account default.
type DocusignNotification = {
  reminders?: { enabled?: boolean; delay?: number; frequency?: number };
  expirations?: { enabled?: boolean; after?: number; warn?: number };
};
export type SendDocusignParams = {
  // UUID strings for simple envelopes, or objects to fill the same template
  // multiple times / route copies to different signers (see DocusignDocument).
  documents?: DocusignDocument[];
  libraryDocuments?: DocusignLibraryDocuments;
  existingEnvelopeId?: string;
  signers?: DocusignSigner[];
  // Copied the completed envelope once every signer finishes; they take no
  // action themselves. Either a bare email or { email, name }. Added to
  // whatever the integration already CCs rather than replacing it. New
  // envelopes only - rejected alongside existingEnvelopeId, which adds
  // documents to an envelope whose recipients are already set.
  ccRecipients?: (string | { email: string; name?: string })[];
  fillData?: Record<string, any>;
  emailSubject?: string;
  emailBlurb?: string;
  draft?: boolean;
  // Designate the envelope for wet (on-paper) signing; signers become optional
  wetSign?: boolean;
  // Show recipients the account's Electronic Record and Signature Disclosure
  useDisclosure?: boolean;
  // Custom reminder/expiration schedule for the envelope
  notification?: DocusignNotification;
  // DocuSign brand profile GUID to apply to the envelope
  brandId?: string;
  // Enforce per-signer document visibility (auto-on when a signer has
  // excludedDocuments)
  enforceSignerVisibility?: boolean;
  // Fill documents only from fillData, ignoring the template's field mapping
  // to the fuser's stored field values
  ignoreTemplateFieldMapping?: boolean;
};
export type GetDocusignEnvelopeParams = {
  envelopeId: string;
};
export type EgnyteFile = {
  name: string;
  data: string; // base64
  // DocuSign's document type (content/summary/certificate), not a MIME type
  type?: string;
};
export type UploadFileToEgnyteParams = {
  // A getDocusignEnvelope document can be passed straight through
  file: EgnyteFile;
  // Relative to the root folder configured on the Egnyte integration
  path?: string;
  name?: string; // overrides file.name
  createFolder?: boolean; // defaults to true
};
export type CreateEgnyteFolderParams = {
  path: string;
};
export type UpdateDocusignEnvelopeParams = {
  envelopeId: string;
  // 'sent' (send a draft) | 'voided' (cancel) | 'discarded' (move to the
  // recycle bin, deleting a draft)
  status: 'sent' | 'voided' | 'discarded';
  voidedReason?: string; // required by the backend when status is 'voided'
};

export interface FormInternalState {
  language: string | undefined;
  currentStep: any;
  latestStepName?: string; // live step key, updated before currentStep.key catches up
  previousStepName: string;
  backNavMap?: Record<string, string>;
  visiblePositions: any;
  logicRules?: LogicRule[];
  assistantClient?: AssistantClient;
  client: FeatheryClient;
  formName: string;
  formId: string;
  fields: Record<string, Field>;
  products: Record<string, SimplifiedProduct>;
  cart: Cart;
  collaborator: Collaborator;
  trackHashes?: boolean;
  formRef: React.MutableRefObject<any>;
  formSettings: any;
  getErrorCallback: (
    props1?: Record<string, unknown>
  ) => (props2?: Record<string, unknown>) => Promise<boolean>;
  navigate: any;
  inlineErrors: InlineErrors;
  setInlineErrors: React.Dispatch<React.SetStateAction<InlineErrors>>;
  // Resolves once an asynchronously-published inline error (e.g. a button
  // submit error) has been written, so programmatic callers can await it.
  pendingInlineErrorPublish?: Promise<void>;
  setUserProgress: React.Dispatch<React.SetStateAction<null>>;
  setDataMappingState?: (state: { show: boolean; hubs: any[] }) => void;
  steps: any;
  setStepKey: (key: string) => void;
  updateFieldOptions: (newOptions: FieldOptions, repeatIndex?: number) => void;
  updateFieldStyles: (fieldKey: string, newStyles: FieldStyles) => void;
  updateFieldProperties: (
    fieldKey: string,
    newProperties: FieldProperties,
    onServar?: boolean
  ) => void;
  setFieldErrors: (
    errors: Record<string, string | { index: number; message: string }>
  ) => void;
  setCalendlyUrl: (url: string) => void;
  runAIExtraction: (
    extractionId: string,
    options: ExtractionActionOptions | boolean,
    pages?: PageSelectionInput
  ) => Promise<Record<string, string>>;
  runComputerAgent: (
    agentId: string,
    options?: RunComputerAgentOptions
  ) => Promise<Record<string, any>>;
  forwardInboxEmail: (options: ForwardInboxEmailOptions) => Promise<void>;
  fillQuikForms: (params: FillQuikParams) => Promise<void>;
  sendDocusignEnvelope: (params: SendDocusignParams) => Promise<void>;
  getDocusignEnvelope: (params: GetDocusignEnvelopeParams) => Promise<any>;
  updateDocusignEnvelope: (
    params: UpdateDocusignEnvelopeParams
  ) => Promise<any>;
  uploadFileToEgnyte: (params: UploadFileToEgnyteParams) => Promise<any>;
  createEgnyteFolder: (params: CreateEgnyteFolderParams) => Promise<any>;
  getDocusignBrands: () => Promise<any>;
  getConfig: GetConfig;
  // Registered by <Form /> so the `feathery.generateDocuments` logic-rule
  // method can run the full Generate Documents flow (generate → optional review
  // modal → envelope action), reaching component-only state like the review
  // viewer. Absent in headless/vanillajs contexts, where the method falls back
  // to the simpler client path.
  generateEnvelopeFlow?: (action: Record<string, any>) => Promise<any>;
  // Registered by <Form /> so a document-editor container can report its
  // signing outcome in the same toast the generate flow uses.
  showEnvelopeOutcome?: (label: string, documents?: string[]) => void;
}

type InternalState = {
  [formUuid: string]: FormInternalState;
};

const internalState: InternalState = {};

// Function that will take a formUuid string and create a new internalState object for the form.
// It also takes an object of stateValues to set on the internalState object for the form
export const setFormInternalState = (
  formUuid: string,
  stateValues: Record<string, any>,
  keysToKeepStable: string[] = []
) => {
  // if the formUuid is already in the internalState object, use it
  const state = internalState[formUuid] ?? {};

  // now overlay each of the stateValues entries onto the corresponding property in the internalState object using Object.assign
  Object.entries(stateValues).forEach(([key, value]) => {
    // if the key in the state object is null or undefined or not and object,
    // then set it to the value from the stateValues object.
    // Otherwise, overlay the obj value from the stateValues object onto the obj value in the state object using Object.assign
    const stateKey: keyof FormInternalState = key as keyof FormInternalState;
    if (
      state[stateKey] === undefined ||
      state[stateKey] === null ||
      typeof state[stateKey] !== 'object' ||
      !keysToKeepStable.includes(stateKey as string)
    )
      state[stateKey] = value;
    else {
      Object.assign(state[stateKey], value);
    }
  });

  // return the form's internalState object
  internalState[formUuid] = state;
  return internalState[formUuid];
};

export default internalState;
